import { useState } from 'react';
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { DialogView, InputField, SelectInput, Button, Notification } from "./index";
import { fetchWarehouseLocationDetails, createLocationBays } from '../services/locationServices';

export const CreateLocationModal = ({ open, setOpen, warehouseId, onSuccess }) => {
  const { t } = useTranslation();

  const buildFallbackCategoryCode = (code) => {
    if (!code) return "";
    return code.toLowerCase().replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase());
  };

  const [formData, setFormData] = useState({
    location_type: '',
    area: '',
    row: '',
    bay_min: '',
    bay_max: '',
    height_min: 'A',
    height_max: 'G',
    join_area_and_row: true,
    skip_existing: true,
    volume_category_id: '',
    max_sku_capacity: '',
    picking_priority: '',
    replace_volume_category: true,
    replace_max_sku_capacity: true,
    replace_picking_priority: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);

  // Fetch location types and volume categories from API
  const { data: locationDetails, isLoading: isLoadingDetails } = useQuery(
    ['warehouse-location-details', warehouseId],
    () => fetchWarehouseLocationDetails(warehouseId),
    {
      enabled: !!warehouseId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const locationTypeOptions = locationDetails?.location_types?.map(type => ({
    id: type,
    name: buildFallbackCategoryCode(type)
  })) || [];

  const volumeCategoryOptions = locationDetails?.volume_categories?.map(category => ({
    id: category.id,
    name: `${buildFallbackCategoryCode(category.category_code)} (Rel. vol. ${category.relative_volume})`
  })) || [];

  // Bay zero-fill width for this warehouse (default 2); pads the preview so it
  // matches the code the backend will store (CUBBO-2206).
  const bayZfill = locationDetails?.bay_zfill_length ?? 2;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    console.log('handleSubmit called!', e);
    e.preventDefault();
    setLoading(true);
    setError(null);

    console.log('Form data:', formData);
    console.log('Warehouse ID:', warehouseId);

    // Validate required fields
    if (!formData.location_type || !formData.area || !formData.row || !formData.bay_min || !formData.bay_max || !formData.volume_category_id) {
      setError(t('location_modals.create_location_modal.validation.required_fields'));
      setLoading(false);
      return;
    }

    // Validate warehouse-specific fields for warehouse 232 or 233
    if ((warehouseId === '232' || warehouseId === '233') && (!formData.max_sku_capacity || !formData.picking_priority)) {
      setError(t('location_modals.create_location_modal.validation.warehouse_specific'));
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        bay_min: parseInt(formData.bay_min),
        bay_max: parseInt(formData.bay_max),
        volume_category_id: parseInt(formData.volume_category_id),
        max_sku_capacity: formData.max_sku_capacity ? parseInt(formData.max_sku_capacity) : null,
        picking_priority: formData.picking_priority ? parseInt(formData.picking_priority) : null
      };

      console.log('Creating locations with payload:', payload);
      console.log('Warehouse ID:', warehouseId);
      
      const result = await createLocationBays(warehouseId, payload);
      console.log('Location creation successful:', result);

      // Show success notification
      setShowSuccessNotification(true);
      
      setOpen(false);
      onSuccess();
      // Reset form
      setFormData({
        location_type: '',
        area: '',
        row: '',
        bay_min: '',
        bay_max: '',
        height_min: 'A',
        height_max: 'G',
        join_area_and_row: true,
        skip_existing: true,
        volume_category_id: '',
        max_sku_capacity: '',
        picking_priority: '',
        replace_volume_category: true,
        replace_max_sku_capacity: true,
        replace_picking_priority: true
      });
    } catch (err) {
      console.error('Error creating locations:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);
      setError(err.response?.data?.message || t('location_modals.create_location_modal.validation.creation_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setOpen(false);
      setError(null);
    }
  };

  return (
    <>
      <Notification
        show={showSuccessNotification}
        setShow={setShowSuccessNotification}
        type="success"
        title={t('location_modals.create_location_modal.success_title')}
        description={t('location_modals.create_location_modal.success_description')}
      />
      <DialogView open={open} setOpen={handleClose} allowOverflow={true}>
      <div style={{ width: '75vh', minWidth: '75vh' }}>
        <div className="mt-3 text-center sm:mt-5">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            {t('location_modals.create_location_modal.title')}
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {t('location_modals.create_location_modal.description')}
          </p>
        </div>

        {isLoadingDetails ? (
          <div className="mt-6 flex justify-center">
            <div className="text-sm text-gray-500">Cargando opciones...</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-6">
            {/* First row: Location Type, Volume Category */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('location_modals.create_location_modal.fields.location_type')} <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  {t('location_modals.create_location_modal.fields.location_type_explanation')}
                </p>
                <SelectInput
                  options={locationTypeOptions}
                  value={formData.location_type ? locationTypeOptions.find(opt => opt.id === formData.location_type) : null}
                  onChange={(value) => handleInputChange('location_type', value.id)}
                  required={true}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('location_modals.create_location_modal.fields.volume_category')} <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  {t('location_modals.create_location_modal.fields.volume_category_explanation')}
                </p>
                <SelectInput
                  options={volumeCategoryOptions}
                  value={formData.volume_category_id ? volumeCategoryOptions.find(opt => opt.id === formData.volume_category_id) : null}
                  onChange={(value) => handleInputChange('volume_category_id', value.id)}
                  required={true}
                />
              </div>
            </div>

            {/* Second row: Area, Row */}
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label={t('location_modals.create_location_modal.fields.area')}
                value={formData.area}
                onChange={(value) => handleInputChange('area', value)}
                placeholder={t('location_modals.create_location_modal.placeholders.area')}
                required={true}
              />

              <InputField
                label={t('location_modals.create_location_modal.fields.row')}
                value={formData.row}
                onChange={(value) => handleInputChange('row', value)}
                placeholder={t('location_modals.create_location_modal.placeholders.row')}
                required={true}
              />
            </div>

            {/* Third row: Bay Min, Bay Max */}
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label={t('location_modals.create_location_modal.fields.bay_min')}
                type="number"
                value={formData.bay_min}
                onChange={(value) => handleInputChange('bay_min', value)}
                placeholder={t('location_modals.create_location_modal.placeholders.bay_min')}
                required={true}
              />

              <InputField
                label={t('location_modals.create_location_modal.fields.bay_max')}
                type="number"
                value={formData.bay_max}
                onChange={(value) => handleInputChange('bay_max', value)}
                placeholder={t('location_modals.create_location_modal.placeholders.bay_max')}
                required={true}
              />
            </div>

            {/* Fourth row: Altura Min, Altura Max */}
            <div className="grid grid-cols-2 gap-4">
              <InputField
                label={t('location_modals.create_location_modal.fields.height_min')}
                value={formData.height_min}
                onChange={(value) => handleInputChange('height_min', value)}
                placeholder={t('location_modals.create_location_modal.placeholders.height_min')}
                required={true}
              />

              <InputField
                label={t('location_modals.create_location_modal.fields.height_max')}
                value={formData.height_max}
                onChange={(value) => handleInputChange('height_max', value)}
                placeholder={t('location_modals.create_location_modal.placeholders.height_max')}
                required={true}
              />
            </div>

            {/* Fifth row: Capacidad Max SKU, Prioridad de Picking - Only for warehouse_id 232 or 233*/}
            {(warehouseId === '232' || warehouseId === '233') && (
              <div className="grid grid-cols-2 gap-4">
                <InputField
                  label={t('location_modals.create_location_modal.fields.max_sku_capacity')}
                  type="number"
                  value={formData.max_sku_capacity}
                  onChange={(value) => handleInputChange('max_sku_capacity', value)}
                  placeholder={t('location_modals.create_location_modal.placeholders.max_sku_capacity')}
                  required={true}
                />

                <InputField
                  label={t('location_modals.create_location_modal.fields.picking_priority')}
                  type="number"
                  value={formData.picking_priority}
                  onChange={(value) => handleInputChange('picking_priority', value)}
                  placeholder={t('location_modals.create_location_modal.placeholders.picking_priority')}
                  required={true}
                />
              </div>
            )}

            {/* Location Code Preview */}
            {formData.area && formData.row && formData.bay_min && formData.bay_max && formData.height_min && formData.height_max && (
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                <h4 className="text-xs font-medium text-gray-600 mb-2">{t('location_modals.create_location_modal.preview.title')}</h4>
                <div className="text-xs text-gray-600 flex items-center gap-2">
                  <span className="font-bold">{t('location_modals.create_location_modal.preview.from')}</span>
                  {formData.area}{formData.row}-{String(formData.bay_min).padStart(bayZfill, '0')}-{formData.height_min}
                  <span className="font-bold">{t('location_modals.create_location_modal.preview.to')}</span>
                  {formData.area}{formData.row}-{String(formData.bay_max).padStart(bayZfill, '0')}-{formData.height_max}
                </div>
                <p className="text-xs text-gray-500 mt-5">
                  {t('location_modals.create_location_modal.preview.format')}
                </p>
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-800">
                        {t('location_modals.create_location_modal.preview.warning')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="secondary"
                onClick={handleClose}
                disabled={loading}
              >
                {t('location_modals.create_location_modal.buttons.cancel')}
              </Button>
              <Button
                type="primary"
                loading={loading}
                loading_label={t('location_modals.create_location_modal.buttons.creating')}
                buttonType="submit"
              >
                {t('location_modals.create_location_modal.buttons.create')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </DialogView>
    </>
  );
};
